import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { patientService } from '@/services/api/patientService';
import type { CreatePatientRequest, UpdatePatientRequest } from '@/types/patient';

export const patientKeys = {
  all: ['patients'] as const,
  lists: () => [...patientKeys.all, 'list'] as const,
  list: (search?: string) => [...patientKeys.lists(), { search }] as const,
  details: () => [...patientKeys.all, 'detail'] as const,
  detail: (id: string) => [...patientKeys.details(), id] as const,
  tests: (id: string) => [...patientKeys.detail(id), 'tests'] as const,
};

/**
 * Hook to search/list patients
 */
export function usePatients(searchQuery?: string) {
  return useQuery({
    queryKey: patientKeys.list(searchQuery),
    queryFn: () => patientService.searchPatients(searchQuery),
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Hook to get patient details
 */
export function usePatient(id: string | null) {
  return useQuery({
    queryKey: patientKeys.detail(id || ''),
    queryFn: () => patientService.getPatient(id!),
    enabled: !!id,
    staleTime: 60_000, // 1 minute
  });
}

/**
 * Hook to get patient test history
 */
export function usePatientTests(id: string | null) {
  return useQuery({
    queryKey: patientKeys.tests(id || ''),
    queryFn: () => patientService.getPatientTests(id!),
    enabled: !!id,
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Hook to create a patient
 */
export function useCreatePatient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePatientRequest) => patientService.createPatient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
    },
  });
}

/**
 * Hook to update a patient
 */
export function useUpdatePatient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePatientRequest }) =>
      patientService.updatePatient(id, data),
    onSuccess: (updatedPatient) => {
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
      queryClient.setQueryData(patientKeys.detail(updatedPatient.id), updatedPatient);
    },
  });
}

/**
 * Hook to delete a patient
 */
export function useDeletePatient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => patientService.deletePatient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
    },
  });
}
